import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, ShoppingBag, X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pool grande de nombres argentinos — cada notificación toma uno DISTINTO al
// anterior. Se recorre en orden aleatorio y sólo se reinicia después de
// haberlos usado a todos (así nunca ves el mismo nombre dos veces cerca).
const SAMPLE_NOTIFICATIONS = [
  { name: 'Antonela', quantity: 1000, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Martín', quantity: 500, service: 'Likes Instagram', platform: 'Instagram' },
  { name: 'Sofía', quantity: 3000, service: 'Likes TikTok', platform: 'TikTok' },
  { name: 'Joaquín', quantity: 100, service: 'Seguidores Facebook', platform: 'Facebook' },
  { name: 'Camila', quantity: 5000, service: 'Visualizaciones TikTok', platform: 'TikTok' },
  { name: 'Tomás', quantity: 10000, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Lucía', quantity: 300, service: 'Likes Instagram', platform: 'Instagram' },
  { name: 'Mateo', quantity: 1000, service: 'Likes TikTok', platform: 'TikTok' },
  { name: 'Valentina', quantity: 500, service: 'Likes Facebook', platform: 'Facebook' },
  { name: 'Bautista', quantity: 5000, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Emma', quantity: 50000, service: 'Visualizaciones TikTok', platform: 'TikTok' },
  { name: 'Felipe', quantity: 1000, service: 'Seguidores Facebook', platform: 'Facebook' },
  { name: 'Julieta', quantity: 2500, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Benjamín', quantity: 200, service: 'Likes Instagram', platform: 'Instagram' },
  { name: 'Isabella', quantity: 15000, service: 'Visualizaciones TikTok', platform: 'TikTok' },
  { name: 'Thiago', quantity: 750, service: 'Likes TikTok', platform: 'TikTok' },
  { name: 'Delfina', quantity: 500, service: 'Seguidores Facebook', platform: 'Facebook' },
  { name: 'Ignacio', quantity: 2000, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Renata', quantity: 400, service: 'Likes Facebook', platform: 'Facebook' },
  { name: 'Ciro', quantity: 8000, service: 'Visualizaciones TikTok', platform: 'TikTok' },
  { name: 'Micaela', quantity: 1500, service: 'Likes Instagram', platform: 'Instagram' },
  { name: 'Franco', quantity: 20000, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Agustina', quantity: 600, service: 'Likes TikTok', platform: 'TikTok' },
  { name: 'Lautaro', quantity: 300, service: 'Seguidores Facebook', platform: 'Facebook' },
  { name: 'Catalina', quantity: 3000, service: 'Likes Instagram', platform: 'Instagram' },
  { name: 'Nicolás', quantity: 25000, service: 'Visualizaciones TikTok', platform: 'TikTok' },
  { name: 'Guadalupe', quantity: 800, service: 'Seguidores Instagram', platform: 'Instagram' },
  { name: 'Máximo', quantity: 5000, service: 'Likes TikTok', platform: 'TikTok' },
];

// Fisher–Yates shuffle inmutable
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Dedupe por nombre (por si el backend manda repetidos), conservando el orden.
const dedupeByName = (list) => {
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const key = (item?.name || '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const PLATFORM_COLORS = {
  Instagram: 'from-pink-500 via-purple-500 to-orange-400',
  TikTok: 'from-cyan-400 to-pink-500',
  Facebook: 'from-blue-600 to-blue-400',
};

const formatNumber = (n) => n.toLocaleString('es-AR');

const getTimeAgo = () => {
  const minutes = Math.floor(Math.random() * 8);
  const seconds = Math.floor(Math.random() * 50) + 10;
  if (minutes === 0) {
    return `hace ${seconds} segundos`;
  }
  return `hace ${minutes} min`;
};

const cleanServiceLabel = (service) => {
  return service
    .replace('Seguidores ', 'seguidores ')
    .replace('Likes ', 'likes ')
    .replace('Visualizaciones ', 'vistas ')
    .toLowerCase();
};

export const SocialProofNotification = () => {
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pool, setPool] = useState(() => dedupeByName(SAMPLE_NOTIFICATIONS));
  const orderRef = useRef(shuffle(dedupeByName(SAMPLE_NOTIFICATIONS)));
  const indexRef = useRef(0);
  const dismissedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_URL}/api/social-proof`)
      .then(({ data }) => {
        if (mounted && data?.notifications?.length >= 3) {
          const merged = dedupeByName([...data.notifications, ...SAMPLE_NOTIFICATIONS]);
          setPool(merged);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Cuando cambia el pool, reshuffle y arrancar de nuevo
  useEffect(() => {
    orderRef.current = shuffle(pool);
    indexRef.current = 0;
  }, [pool]);

  useEffect(() => {
    if (dismissedRef.current) return;

    const initialTimer = setTimeout(() => showNext(), 5000);
    const cycleTimer = setInterval(() => {
      if (!dismissedRef.current) showNext();
    }, 14000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(cycleTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool]);

  const showNext = () => {
    const order = orderRef.current;
    if (!order || order.length === 0) return;

    // Si ya recorrimos toda la lista, reshuffle y seguimos (garantizando además
    // que el primer nombre del nuevo ciclo no sea igual al último mostrado).
    if (indexRef.current >= order.length) {
      const lastName = current?.name;
      let next = shuffle(order);
      if (lastName && next[0]?.name === lastName && next.length > 1) {
        [next[0], next[1]] = [next[1], next[0]];
      }
      orderRef.current = next;
      indexRef.current = 0;
    }

    const item = orderRef.current[indexRef.current];
    indexRef.current += 1;
    setCurrent({ ...item, timeAgo: getTimeAgo() });
    setVisible(true);
    setTimeout(() => setVisible(false), 6000);
  };

  const handleDismiss = () => {
    setVisible(false);
    dismissedRef.current = true;
  };

  if (!current) return null;

  const colorClass = PLATFORM_COLORS[current.platform] || 'from-primary to-primary';

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 sm:right-auto z-40 sm:max-w-sm transition-all duration-400 ${
        visible ? 'animate-slide-in-up opacity-100' : 'animate-slide-out-down opacity-0 pointer-events-none'
      }`}
      data-testid="social-proof-notification"
    >
      <div className="relative rounded-xl bg-card border border-border shadow-2xl p-4 pr-10 backdrop-blur-md">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
          aria-label="Cerrar"
        >
          <X size={12} className="text-muted-foreground" />
        </button>
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center shrink-0`}
          >
            <ShoppingBag size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-snug">
              <span className="font-semibold">{current.name}</span>{' '}
              <span className="text-muted-foreground">compró</span>{' '}
              <span className="font-semibold">
                {formatNumber(current.quantity)} {cleanServiceLabel(current.service)}
              </span>
            </p>
            <div className="flex items-center gap-1 mt-1">
              <CheckCircle size={12} weight="fill" className="text-[#00E676]" />
              <span className="text-xs text-muted-foreground">{current.timeAgo}</span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-[#00E676] font-medium">verificado</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
